import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Sparkle, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { useDraggable } from "@/hooks/useDraggable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "ca_reading_alert_dismissed_for";

export const ReadingFloat = () => {
  const { user, authHeader } = useAuth();
  const location = useLocation();
  const [active, setActive] = useState(null); // {id, status} or null
  const [showAlert, setShowAlert] = useState(true);
  const drag = useDraggable();

  const fetchActive = async () => {
    try {
      const r = await axios.get(`${API}/users/reading/active`, { headers: authHeader() });
      const sess = r.data?.session || null;
      // Only show the float for ongoing readings (not yet completed/cancelled).
      const isOngoing = sess && (sess.status === "granted" || sess.status === "in_progress");
      setActive(r.data?.active && isOngoing ? sess : null);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user) { setActive(null); return undefined; }
    fetchActive();
    const t = window.setInterval(fetchActive, 30000);
    const onFocus = () => fetchActive();
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(t);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line
  }, [user]);

  // Real-time: instantly refresh on any user-targeted notification
  useRealtimeEvents({ notification: fetchActive });

  // Show blinking alert only when reading is granted (not yet started)
  // and user hasn't dismissed this specific session.
  useEffect(() => {
    if (!active) return;
    try {
      const dismissed = localStorage.getItem(DISMISS_KEY);
      setShowAlert(dismissed !== active.id);
    } catch { setShowAlert(true); }
  }, [active]);

  if (!user || !active) return null;
  // Hide on the Lectura Online page itself — the user is already there.
  if (location.pathname.startsWith("/lecturaonline")) return null;

  const isGranted = active.status === "granted";

  const dismissAlert = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowAlert(false);
    try { localStorage.setItem(DISMISS_KEY, active.id); } catch { /* noop */ }
  };

  return (
    <div {...drag} data-testid="reading-float-root" className="fixed bottom-64 left-6 z-[60] flex items-center gap-3 cursor-grab active:cursor-grabbing">
      <Link
        to="/lecturaonline"
        data-testid="reading-float"
        className="group"
        title="Tu lectura está habilitada"
      >
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-purple-500/40 blur-xl animate-pulse" />
          <div className="relative flex items-center gap-2 bg-gradient-to-br from-purple-600 to-fuchsia-700 text-white px-4 py-3 rounded-full shadow-2xl shadow-purple-500/50 border border-purple-300/30 hover:scale-105 transition-transform">
            <Sparkle className="w-5 h-5 animate-spin-slow" />
            <span className="text-xs uppercase tracking-widest font-semibold hidden md:inline">Mi Lectura</span>
          </div>
        </div>
      </Link>

      {isGranted && showAlert && (
        <div
          data-testid="reading-blink-alert"
          className="relative hidden sm:flex items-center gap-2 bg-gradient-to-r from-[#d4af37] to-amber-500 text-black font-semibold px-4 py-2 rounded-full shadow-lg shadow-amber-500/30 animate-pulse"
          style={{ animationDuration: "1.2s" }}
        >
          <span className="text-xs">👉 Da clic al botón para iniciar tu lectura</span>
          <button
            onClick={dismissAlert}
            data-testid="reading-blink-dismiss"
            className="ml-1 -mr-1 hover:scale-110 transition-transform"
            aria-label="Cerrar aviso"
            title="Cerrar"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};
